import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { filter, takeUntil, take, tap } from 'rxjs/operators';

import { DocumentVersionInput, Customer, ContactPerson } from 'graphql/models/generated';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { StoreService } from 'shared/services/store.service';

import { ContactPersonBasicInfoComponent } from '../basic-info/basic-info.component';

@Component({
  selector: 'app-contact-person-basic-info-modal',
  templateUrl: './basic-info-modal.component.html',
  styleUrls: ['./basic-info-modal.component.scss']
})
export class ContactPersonBasicInfoModalComponent extends BaseComponent implements OnInit {
  @ViewChild(ContactPersonBasicInfoComponent) basicInfoComponent!: ContactPersonBasicInfoComponent;

  docVersion!: DocumentVersionInput;

  customerId!: string;

  contactPerson!: ContactPerson;

  private isFirstContactPerson = false;

  get form(): UntypedFormGroup {
    return this.basicInfoComponent.form;
  }

  constructor(
    protected coreDataService: CoreDataService,
    protected storeService: StoreService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.storeService.getIsFirstContactPerson()
      .pipe(take(1))
      .subscribe(value => {
        this.isFirstContactPerson = !!value;
      });

    this.storeService.getCurrentContactPerson()
      .pipe(take(1))
      .subscribe((data: ContactPerson) => {
        this.contactPerson = data || {};
      });

    // modal in core-data context
    // used if cp is added during creation of a new customer
    this.storeService.getCurrentCustomer()
      .pipe(filter((c: Customer) => Boolean(c && c.id)), takeUntil(this.destroyed$))
      .subscribe((customer: Customer) => {
        this.customerId = customer.id as string;
      });

    // modal in offer context
    this.storeService.getCurrentOffer()
      .pipe(filter((o) => Boolean(o && o.documentId)), takeUntil(this.destroyed$))
      .subscribe((o) => {
        this.docVersion = { documentId: o.documentId, revision: o.revision };

        if (o.customerId) {
          // Input 'item' (customer) is not available when opening the 'contactPerson-form' modal
          // from the 'contactPerson' box in the offer header,
          // so the only way to get and set the 'customerId' is from the current store offer
          this.customerId = o.customerId;
        }
      });
  }

  onSubmit(): Observable<ContactPerson> {
    if (this.contactPerson.id) {
      return this.coreDataService.updateContactPerson(this.contactPerson.id, this.form.value);
    }

    return this.coreDataService.createContactPerson(
      { ...this.form.value, isMain: this.isFirstContactPerson },
      this.customerId,
      this.docVersion
    ).pipe(
      tap((cp: ContactPerson) => this.appendContactPersonToCurrentCustomer(cp))
    );
  }

  private appendContactPersonToCurrentCustomer(cp: ContactPerson): void {
    this.storeService.getCurrentCustomer()
      .pipe(take(1))
      .subscribe((customer: Customer) => {
        if (!customer || !customer.id) {
          return;
        }
        this.storeService.setCurrentDataItem({
          ...customer,
          contactPersons: [...(customer.contactPersons ?? []), cp]
        });
      });
  }
}
